/**
 * iter84.56.8 — Tech-debt split from ReleaseOpsAuditCards.jsx.
 * Mechanical extraction; no behavior change.
 */
import { useCallback, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Loader2, ClipboardCheck,
    Zap, ExternalLink,
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { CopyDiagButton, buildDiagText, diagLine } from "@/components/site-admin/release-ops/CopyDiagButton";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";
import { StatusPill } from "@/components/site-admin/audit-cards/_shared";

export function ParitySweepCard({ environment = "preview", onVerdictResolved, onOpenGameOps }) {
    const [data, setData] = useState(null);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    // iter84.56.6.5 — Ack state. `ackTarget` is the check row being acked.
    const [ackTarget, setAckTarget] = useState(null);
    const [ackReason, setAckReason] = useState("");
    const [acking, setAcking] = useState(false);
    // iter84.72 — Inline WCKO stage heal (idempotent, audit-logged).
    const [healingWcko, setHealingWcko] = useState(false);

    const healWcko = async () => {
        if (healingWcko) return;
        setHealingWcko(true);
        try {
            const r = await api.releaseOpsHealWckoStages();
            toast.success(`Healed ${r.total_teams_patched ?? 0} team(s) across ${r.tournaments_patched ?? 0} tournament(s)`);
            await refreshAfterAckChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "WCKO heal failed");
        }
        setHealingWcko(false);
    };

    const runSweep = useCallback(async () => {
        if (running) return;
        setRunning(true);
        setError(null);
        try {
            const r = await api.releaseOpsParitySweep();
            setData(r);
            const s = r.summary || {};
            if (r.overall === "pass") {
                toast.success(`Parity sweep clean — ${s.passed}/${s.total} checks pass.`);
            } else if (r.overall === "warn") {
                toast(`Parity sweep: ${s.warned} warn, ${s.passed} pass${s.acked ? ", " + s.acked + " ACK'd" : ""}.`, { icon: "⚠️" });
            } else {
                toast.error(`Parity sweep: ${s.failed} FAIL — review before arming.`);
            }
            // iter84.56.7-e — Hand the verdict back so the parent can
            // auto-tick the parity_sweep_clear gate when clean. Operator
            // decisions are preserved (parent only flips gates that are
            // currently pending).
            if (onVerdictResolved) {
                onVerdictResolved({ overall: r.overall, summary: s });
            }
        } catch (e) {
            setError(e.response?.data?.detail || "Sweep failed");
            toast.error("Parity sweep request failed");
        } finally {
            setRunning(false);
        }
    }, [running, onVerdictResolved]);

    // Refresh a single check row inline (avoids re-running the whole sweep
    // just to reflect an ack/revoke). We just re-run the full sweep —
    // it's cheap and keeps state consistent.
    const refreshAfterAckChange = useCallback(async () => {
        try {
            const r = await api.releaseOpsParitySweep();
            setData(r);
        } catch {
            // Silent fallback — operator can manually click Re-run.
        }
    }, []);

    const createAck = async () => {
        if (!ackTarget || !ackReason.trim()) return;
        setAcking(true);
        try {
            await api.releaseOpsCreateAuditAck({
                check_id: ackTarget.id,
                signature: ackTarget.signature,
                reason: ackReason.trim(),
            });
            toast.success(`Acknowledged — ${ackTarget.label} won't fail the sweep until state changes.`);
            setAckTarget(null);
            setAckReason("");
            await refreshAfterAckChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Acknowledge failed");
        } finally {
            setAcking(false);
        }
    };

    const revokeAck = async (ackId, label) => {
        try {
            await api.releaseOpsRevokeAuditAck(ackId);
            toast.success(`Revoked — ${label} will surface as FAIL/WARN on the next sweep.`);
            await refreshAfterAckChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Revoke failed");
        }
    };

    const verdict = error
        ? "fail"
        : data
            ? (data.overall === "pass" ? "clear" : data.overall === "warn" ? "warn" : "fail")
            : "clear";

    const badge = data
        ? `${data.summary.passed}/${data.summary.total} pass · ${data.summary.warned} warn · ${data.summary.failed} fail${(data.summary?.acked || 0) > 0 ? " · " + data.summary.acked + " ack" : ""}`
        : (environment === "production" ? "production" : "preview");
    const badgeClass = !data
        ? "bg-zinc-700/40 text-zinc-300 border-zinc-600/40"
        : data.overall === "pass"
            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
            : data.overall === "warn"
                ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                : "bg-rose-500/15 text-rose-300 border-rose-500/40";

    const runBtn = (
        <span className="flex items-center gap-1">
            <WhatsThisButton
                testId="parity-sweep-whats-this"
                title="Pre-deploy sweep"
                what="A one-click scan of GAME DATA before you ship: orphaned picks, leftover test records, duplicate matches, bracket stage stamps, plus a rollup of the Product Ops game-health detectors. Read-only — it never changes anything."
                lookFor="PASS, or warns you've consciously acknowledged (ack). A FAIL means broken game data — fix it in Product Ops before deploying."
                notFor="Infrastructure or config problems (that's Deployment Readiness) and engineering-discipline checks (that's the Values Engine)."
            />
            {data && (
                <CopyDiagButton
                    testId="parity-sweep-copy-diag-btn"
                    getText={() => buildDiagText({
                        card: "Pre-deploy sweep (parity + game health)",
                        environment: data.environment || environment,
                        verdict: data.overall,
                        summary: `${data.summary.passed}/${data.summary.total} pass · ${data.summary.warned} warn · ${data.summary.failed} fail${(data.summary?.acked || 0) > 0 ? ` · ${data.summary.acked} ack` : ""}`,
                        lines: (data.checks || [])
                            .filter((c) => c.status !== "pass")
                            .map((c) => diagLine(c.status, c.label, [c.summary, c.detail].filter(Boolean).join(" · "))),
                    })}
                />
            )}
            {/* iter84.56.6.3 — Direct link to the raw JSON. Same-origin
                so the HttpOnly `site_admin_token` cookie tags along
                automatically. Opens in a new tab so the operator's
                Release Ops view isn't disturbed. */}
            <a
                href="/api/release-ops/parity-sweep"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid="parity-sweep-raw-json-link"
                className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer no-underline"
                title="Open the raw /api/release-ops/parity-sweep response in a new tab"
            >
                <ExternalLink className="w-3 h-3" />
                RAW JSON
            </a>
            <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); runSweep(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); runSweep(); } }}
                data-testid="parity-sweep-run-btn"
                className="brutal-btn-volt text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer disabled:opacity-50"
            >
                {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                {running ? "Sweeping…" : data ? "Re-run" : "Run sweep"}
            </span>
        </span>
    );

    return (
        <CollapsibleCard
            testId="parity-sweep-card"
            icon={Zap}
            title="Pre-deploy sweep · parity + game health"
            badge={badge}
            badgeClass={badgeClass}
            verdict={verdict}
            headerExtra={runBtn}
            defaultOpen
        >
            <div
                data-environment={environment}
                data-overall={data?.overall || "unknown"}
            >
                <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                    Rolls up every <span className="font-bold text-white">Game Health Registry domain</span> (Picks · Scoring · Data Integrity · Upstream Sync · Catalog) plus <span className="font-bold text-white">Test pollution</span> into one shot — same detectors that power Product Ops, so there's one source of truth. Read-only — never mutates. Use <span className="text-cyber">Fix in Product Ops →</span> on a row to open the Issues queue with its paired healers.
                </p>
                {!data && !running && !error && (
                    <p
                        data-testid="parity-sweep-not-run"
                        className="text-[11px] text-zinc-500 italic flex items-center gap-1.5"
                    >
                        <Zap className="w-3.5 h-3.5" />
                        Click <span className="font-bold text-volt">Run sweep</span> to verify pre-deploy health.
                    </p>
                )}
                {running && (
                    <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Sweeping game-health domains…
                    </p>
                )}
                {error && (
                    <p className="text-xs text-rose-400" data-testid="parity-sweep-error">
                        {error}
                    </p>
                )}
                {data && !running && !error && (
                    <>
                        <ul className="space-y-1.5" data-testid="parity-sweep-rows">
                            {data.checks.map((c) => (
                                <li
                                    key={c.id}
                                    data-testid={`parity-sweep-row-${c.id}`}
                                    data-status={c.status}
                                    className={`border rounded-sm p-2 ${
                                        c.status === "pass"
                                            ? "border-emerald-500/25 bg-emerald-500/5"
                                            : c.status === "warn"
                                                ? "border-amber-500/30 bg-amber-500/5"
                                                : c.status === "ack"
                                                    ? "border-cyan-500/30 bg-cyan-500/5"
                                                    : "border-rose-500/30 bg-rose-500/5"
                                    }`}
                                >
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <StatusPill status={c.status} />
                                        <span className="font-bold text-white text-[11px]">{c.label}</span>
                                        <span className="ml-auto flex items-center gap-2 shrink-0">
                                            {/* iter84.56.6.5 — Acknowledge button for FAIL/WARN rows. */}
                                            {(c.status === "fail" || c.status === "warn") && c.signature && c.signature !== "clean" && c.signature !== "error" && (
                                                <button
                                                    type="button"
                                                    onClick={() => setAckTarget(c)}
                                                    data-testid={`parity-sweep-row-${c.id}-ack-btn`}
                                                    className="text-[10px] text-cyan-300 hover:underline shrink-0 font-bold uppercase tracking-widest"
                                                    title="Acknowledge this state. Logs a reason + auto-invalidates if state changes."
                                                >
                                                    Acknowledge
                                                </button>
                                            )}
                                            {c.status === "ack" && c.ack && (
                                                <button
                                                    type="button"
                                                    onClick={() => revokeAck(c.ack.id, c.label)}
                                                    data-testid={`parity-sweep-row-${c.id}-revoke-btn`}
                                                    className="text-[10px] text-zinc-400 hover:text-rose-300 hover:underline shrink-0 uppercase tracking-widest"
                                                    title="Revoke this acknowledgement. The row will go back to FAIL/WARN."
                                                >
                                                    Revoke
                                                </button>
                                            )}
                                            {/* iter84.72 — Deep-link into Product Ops
                                                (registry rows) instead of dead same-page
                                                anchors; pollution keeps a raw-JSON link. */}
                                            {c.id.startsWith("gh_") && onOpenGameOps && c.status !== "pass" && (
                                                <button
                                                    type="button"
                                                    onClick={() => onOpenGameOps({ sub: "issues" })}
                                                    data-testid={`parity-sweep-row-${c.id}-fix-btn`}
                                                    className="text-[10px] text-cyber hover:underline shrink-0"
                                                >
                                                    Fix in Product Ops →
                                                </button>
                                            )}
                                            {(c.data?.finding_ids || []).includes("wcko_stage_integrity") && (
                                                <button
                                                    type="button"
                                                    disabled={healingWcko}
                                                    onClick={healWcko}
                                                    data-testid={`parity-sweep-row-${c.id}-heal-wcko-btn`}
                                                    className="text-[10px] font-bold text-emerald-300 border border-emerald-700 rounded px-1.5 py-0.5 hover:bg-emerald-900/20 shrink-0 disabled:opacity-50"
                                                >
                                                    {healingWcko ? "Healing…" : "Heal now"}
                                                </button>
                                            )}
                                            {c.id === "test_pollution" && c.status !== "pass" && (
                                                <a
                                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/release-ops/test-pollution-inventory`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`parity-sweep-row-${c.id}-raw-btn`}
                                                    className="text-[10px] text-cyber hover:underline shrink-0"
                                                >
                                                    Raw inventory →
                                                </a>
                                            )}
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-zinc-300 mt-1">{c.summary}</p>
                                    {c.detail && (
                                        <p className="text-[9px] text-zinc-500 italic mt-0.5">{c.detail}</p>
                                    )}
                                    {c.status === "ack" && c.ack && (
                                        <p
                                            data-testid={`parity-sweep-row-${c.id}-ack-meta`}
                                            className="text-[10px] text-cyan-300 mt-1.5 italic border-t border-cyan-500/20 pt-1"
                                        >
                                            ACK by <span className="font-bold not-italic">{c.ack.acked_by}</span>{" "}
                                            on <span className="not-italic">{new Date(c.ack.acked_at).toLocaleString()}</span> — "{c.ack.reason}"
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <p className="text-[10px] text-zinc-500 italic mt-2 font-mono">
                            swept {new Date(data.scanned_at).toLocaleString()} · env: {data.environment}
                            {(data.summary?.acked || 0) > 0 && (
                                <span> · {data.summary.acked} ACK'd</span>
                            )}
                        </p>
                    </>
                )}
            </div>

            {/* iter84.56.6.5 — Acknowledge confirm modal. Reason required.
                Backend stores (check_id + signature + reason). On subsequent
                sweeps, if signature still matches, row appears as ACK. If
                the underlying state changes, the signature flips and the
                ack auto-invalidates, surfacing the new state as FAIL again. */}
            {ackTarget && (
                <div
                    data-testid="parity-sweep-ack-modal"
                    onClick={() => !acking && setAckTarget(null)}
                    className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-zinc-950 border-2 border-cyan-500/60 shadow-2xl rounded-sm max-w-lg w-full p-4"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <ClipboardCheck className="w-5 h-5 text-cyan-300" />
                            <h3 className="text-sm font-bold tracking-widest uppercase text-white">Acknowledge audit issue</h3>
                        </div>
                        <p className="text-[11px] text-zinc-300 mb-2 leading-relaxed">
                            Acknowledging <span className="font-bold text-white">{ackTarget.label}</span>. This will demote the row from <span className="font-bold text-rose-300">{ackTarget.status.toUpperCase()}</span> to <span className="font-bold text-cyan-300">ACK'd</span> on future sweeps until the underlying state changes (then it auto-invalidates).
                        </p>
                        <p className="text-[10px] text-zinc-500 mb-3 font-mono">
                            Current state: <span className="text-zinc-300">{ackTarget.summary}</span>
                            <br />
                            Signature: <span className="text-cyber">{ackTarget.signature}</span>
                        </p>
                        <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Reason (required — logged forever)</label>
                        <textarea
                            data-testid="parity-sweep-ack-reason"
                            value={ackReason}
                            onChange={(e) => setAckReason(e.target.value)}
                            disabled={acking}
                            placeholder="e.g. Known iter84.56.4 artifact in WCKO 54dce8, leaving alone — JC"
                            rows={3}
                            className="brutal-input w-full text-[11px] resize-none"
                        />
                        <div className="flex items-center justify-end gap-2 mt-3">
                            <button
                                type="button"
                                onClick={() => setAckTarget(null)}
                                disabled={acking}
                                data-testid="parity-sweep-ack-cancel"
                                className="brutal-btn-ghost text-[10px] py-1 px-3 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={createAck}
                                disabled={acking || !ackReason.trim()}
                                data-testid="parity-sweep-ack-confirm"
                                className="brutal-btn-volt text-[10px] py-1 px-3 inline-flex items-center gap-1 disabled:opacity-50"
                            >
                                {acking ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardCheck className="w-3 h-3" />}
                                {acking ? "Saving…" : "Confirm acknowledge"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </CollapsibleCard>
    );
}


// ---------------------------------------------------------------------------
// iter84.56.6.4 — ScoringDriftHealCard
//
// Wraps the iter84.56.6.3 migration endpoint
// (POST /api/release-ops/zero-entry-stage-reach-points) in a self-service
// UI surface so operators don't need DevTools / curl to run it.
//
// Pre-flight dry-run via GET /api/release-ops/scoring-drift-preview shows
// the impact list BEFORE the operator clicks Heal. Confirm modal lists
// each affected tournament + the field about to be zeroed. Idempotent —
// re-running on a clean DB is a no-op.
//
// Applies generally to every tournament regardless of template (WC KO,
// March Madness, custom KO templates, etc.) — the rule keys on
// `stages[0].key`, not on template name.
// ---------------------------------------------------------------------------

